/**
 * Receiving the handoff from "Sign in with ERPNext".
 *
 * The server finishes the OAuth exchange and sends the browser back here with
 * the session in the URL *fragment* — the one part of a URL browsers never
 * transmit, so it stays out of server logs, referrer headers and any proxy in
 * between. The routing history is createWebHistory, so the fragment is ours to
 * use.
 *
 * This runs before the app mounts: the token has to be in place before the
 * router's guard decides whether to show the console or bounce to sign-in.
 */

import { setSession } from "./api";
import { apiUrl, BACKEND_ORIGIN } from "./backend";

/** Survives the redirect so the sign-in screen can explain what went wrong. */
let pendingError: string | null = null;

/** Whether the sign-in screen has something to explain, without taking it. */
export function hasErpSignInError(): boolean {
  return pendingError !== null;
}

export function takeErpSignInError(): string | null {
  const error = pendingError;
  pendingError = null;
  // The screen has now shown this one; a fresh failure earns a fresh bounce.
  reauthTriggered = false;
  return error;
}

/**
 * A screen's own ERP tool call came back "unauthenticated" — the credential
 * Genie holds for this operator's ERPNext identity, not their Genie sign-in,
 * has expired or been replaced. Every screen used to just print the tool's
 * error text and leave the operator free to click elsewhere, which is wrong:
 * every other ERP call on the site will fail the same way, so the console
 * bounces here rather than pretending some screens still work.
 *
 * Latched so a burst of parallel calls that all fail at once (a page that
 * fires several tool calls on mount) triggers one redirect, not several; the
 * latch clears once the sign-in screen has shown the message (see
 * `takeErpSignInError`), so the next failure gets its own bounce.
 */
let reauthTriggered = false;
let onReauthNeeded: (() => void) | null = null;

/** Wired once at startup (see main.ts) — kept separate so this module need not import the router. */
export function onErpReauthNeeded(callback: () => void): void {
  onReauthNeeded = callback;
}

export function flagErpReauthNeeded(message: string): void {
  if (reauthTriggered) return;
  reauthTriggered = true;
  pendingError = message;
  onReauthNeeded?.();
}

/**
 * Consumes an ERP sign-in fragment if one is present.
 *
 * Returns the path to land on, or null when this was an ordinary page load.
 * The fragment is stripped either way — a session token has no business
 * sitting in the address bar, in the back/forward history, or in whatever the
 * operator pastes into a chat window later.
 */
export function consumeErpSignIn(): string | null {
  const raw = window.location.hash.replace(/^#/, "");
  if (!raw) return null;

  const params = new URLSearchParams(raw);
  const token = params.get("token");
  const error = params.get("erp_error");
  if (!token && !error) return null;

  // replaceState rather than assigning location.hash: assigning would leave the
  // token in the history entry we are trying to erase.
  const clean = window.location.pathname + window.location.search;
  window.history.replaceState(null, "", clean);

  if (error) {
    pendingError = error;
    return "/signin";
  }

  setSession({ token: token!, refreshToken: params.get("refresh_token") ?? undefined });

  const redirectTo = params.get("redirect_to");
  return redirectTo && /^\/[^/\\]/.test(redirectTo) ? redirectTo : "/";
}

/** Hands the browser to the server, which hands it to ERPNext. */
export function startErpSignIn(redirectTo?: string, site?: string): void {
  const params = new URLSearchParams();
  if (redirectTo) params.set("redirect_to", redirectTo);
  // Which configured site to sign into; the server refuses unknown ones with
  // a readable reason, since browser sign-in needs that site's OAuth client.
  if (site) params.set("site", site);
  const qs = params.toString();
  const path = qs ? `/api/auth/erp/start?${qs}` : "/api/auth/erp/start";

  // Desktop: the renderer runs on an app:// origin where a relative "/api/..."
  // resolves to nothing, so the start URL is absolutized to the injected backend
  // origin via apiUrl (which prepends BACKEND_ORIGIN; on web BACKEND_ORIGIN is ""
  // and the path stays byte-for-byte relative). A same-window full-page nav would
  // also carry the Electron window AWAY from the app:// app shell to the loopback
  // origin — and the callback redirects the browser back to an http(s) web origin
  // (resolveAppUrl in server/routes/erp-auth.ts, never app://), so the token would
  // land in a plain server page, not the Electron bundle. We therefore hand the
  // absolute URL to the system browser instead.
  //
  // KNOWN LIMITATION: on desktop this only STARTS the flow (the operator sees the
  // real ERPNext login). Completing the round-trip back into Electron needs a
  // go1genie:// deep-link callback + backend redirect-URI support — see the
  // desktop backlog follow-up. Desktop v1 defaults to mock ERP (offline), so this
  // live-ERP path is off the critical path.
  if (BACKEND_ORIGIN) {
    const bridge = (window as unknown as {
      genieDesktop?: { openExternal: (url: string) => Promise<boolean> };
    }).genieDesktop;
    void bridge?.openExternal(apiUrl(path));
    return;
  }

  // Web: a full navigation, not fetch: the operator has to see and trust the
  // ERPNext login page on its own origin.
  window.location.assign(path);
}
